import NavigationBreadcrumbsSkeleton from './Skeleton';
import { NavigationBreadcrumbsProps } from './props';
import styles from './styles.module.scss';
import { ErrorChip } from '../ErrorChip';
import { Link } from '../Link';

/**
 * NavigationBreadcrumbs Component
 * A component that displays a breadcrumb navigation with a link to the previous page and current page label.
 * Handles loading and error states, with ability to retry on error.
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} props.currentPageLabel - Label for the current page
 * @param {string} props.lastPageLabel - Label for the previous/parent page
 * @param {string} props.to - URL path to navigate to when clicking the last page link
 * @param {boolean} props.isLoading - Whether the breadcrumbs are in loading state
 * @param {boolean} props.isError - Whether there was an error loading the breadcrumbs
 * @param {() => void} [props.refetch] - Optional callback to retry loading on error
 * @returns {JSX.Element} The rendered breadcrumb navigation
 *
 * @example
 * // Basic usage
 * <NavigationBreadcrumbs
 *   currentPageLabel="Product Details"
 *   lastPageLabel="Products"
 *   to="/products"
 *   isLoading={false}
 *   isError={false}
 * />
 *
 * // With loading state
 * <NavigationBreadcrumbs
 *   currentPageLabel="User Profile"
 *   lastPageLabel="Users"
 *   to="/users"
 *   isLoading={true}
 *   isError={false}
 * />
 *
 * // With error and retry
 * <NavigationBreadcrumbs
 *   currentPageLabel="Dashboard"
 *   lastPageLabel="Home"
 *   to="/"
 *   isLoading={false}
 *   isError={true}
 *   refetch={() => fetchData()}
 * />
 */
export function NavigationBreadcrumbs({
  currentPageLabel,
  lastPageLabel,
  to,
  isLoading,
  isError,
  refetch,
}: NavigationBreadcrumbsProps) {
  /**
   * Handles error retry
   * Calls the refetch callback if provided
   */
  function handleError() {
    refetch?.();
  }

  if (isLoading) {
    return <NavigationBreadcrumbsSkeleton />;
  }

  if (isError) {
    return <ErrorChip action={handleError} />;
  }

  return (
    <div className={styles.container}>
      <Link className={styles.container__link} href={to}>
        {lastPageLabel}
      </Link>

      <i className={`${styles.container__icon} fa-solid fa-chevron-right`} />
      <h1 className={styles.container__text}>{currentPageLabel}</h1>
    </div>
  );
}
